function getBinaryBytes(data) {
    if (data instanceof Uint8Array) {
        return data;
    }

    if (ArrayBuffer.isView(data)) {
        return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    }

    if (data instanceof ArrayBuffer) {
        return new Uint8Array(data);
    }

    return new Uint8Array(0);
}

function getTextPrefix(data, maxBytes = 1024) {
    const bytes = getBinaryBytes(data).slice(0, maxBytes);

    if (bytes.length === 0) {
        return '';
    }

    if (typeof TextDecoder !== 'undefined') {
        return new TextDecoder('utf-8').decode(bytes);
    }

    return Array.from(bytes)
        .map((byte) => String.fromCharCode(byte))
        .join('');
}

function getNormalizedTextPrefix(data, maxBytes = 1024) {
    return getTextPrefix(data, maxBytes)
        .trim()
        .toLowerCase();
}

function getNormalizedContentType(resource) {
    return String(resource?.contentType || '')
        .trim()
        .toLowerCase();
}

function isLikelyBinaryStl(bytes) {
    if (bytes.length < 84) {
        return false;
    }

    const dataView = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const triangleCount = dataView.getUint32(80, true);
    const expectedByteLength = 84 + (triangleCount * 50);

    return triangleCount > 0 && expectedByteLength <= bytes.length;
}

export function detectImageContentTypeFromBuffer(buffer) {
    const bytes = getBinaryBytes(buffer);

    if (
        bytes.length >= 8
        && bytes[0] === 0x89
        && bytes[1] === 0x50
        && bytes[2] === 0x4E
        && bytes[3] === 0x47
        && bytes[4] === 0x0D
        && bytes[5] === 0x0A
        && bytes[6] === 0x1A
        && bytes[7] === 0x0A
    ) {
        return 'image/png';
    }

    if (
        bytes.length >= 3
        && bytes[0] === 0xFF
        && bytes[1] === 0xD8
        && bytes[2] === 0xFF
    ) {
        return 'image/jpeg';
    }

    if (
        bytes.length >= 6
        && bytes[0] === 0x47
        && bytes[1] === 0x49
        && bytes[2] === 0x46
        && bytes[3] === 0x38
        && (bytes[4] === 0x39 || bytes[4] === 0x37)
        && bytes[5] === 0x61
    ) {
        return 'image/gif';
    }

    if (
        bytes.length >= 12
        && bytes[0] === 0x52
        && bytes[1] === 0x49
        && bytes[2] === 0x46
        && bytes[3] === 0x46
        && bytes[8] === 0x57
        && bytes[9] === 0x45
        && bytes[10] === 0x42
        && bytes[11] === 0x50
    ) {
        return 'image/webp';
    }

    if (bytes.length >= 2 && bytes[0] === 0x42 && bytes[1] === 0x4D) {
        return 'image/bmp';
    }

    const textStart = getNormalizedTextPrefix(bytes, 128);

    if (textStart.startsWith('<?xml') || textStart.startsWith('<svg')) {
        return 'image/svg+xml';
    }

    return '';
}

export function validateImageBinaryResource(resource) {
    const detectedContentType = detectImageContentTypeFromBuffer(resource?.data);

    if (!detectedContentType) {
        throw new Error('Image response was not a recognized image.');
    }

    return {
        contentType: detectedContentType,
    };
}

export function validatePdfBinaryResource(resource) {
    const normalizedTextPrefix = getNormalizedTextPrefix(resource?.data, 1024);

    if (!normalizedTextPrefix.includes('%pdf-')) {
        throw new Error('PDF response was not a recognized PDF.');
    }

    return {
        contentType: 'application/pdf',
    };
}

export function validateGeometryBinaryResource(resource, expectedFormat) {
    const bytes = getBinaryBytes(resource?.data);

    if (bytes.length === 0) {
        throw new Error('Geometry response was empty.');
    }

    const normalizedTextPrefix = getNormalizedTextPrefix(bytes, 256);

    if (expectedFormat === 'ply') {
        if (!normalizedTextPrefix.startsWith('ply')) {
            throw new Error('Geometry response was not a recognized PLY file.');
        }

        return {
            contentType: getNormalizedContentType(resource) || 'application/octet-stream',
        };
    }

    const isAsciiStl = normalizedTextPrefix.startsWith('solid');
    const isBinaryStl = isLikelyBinaryStl(bytes);

    if (!isAsciiStl && !isBinaryStl) {
        throw new Error('Geometry response was not a recognized STL file.');
    }

    return {
        contentType: getNormalizedContentType(resource) || 'application/sla',
    };
}

export function validatePlyBinaryResource(resource) {
    return validateGeometryBinaryResource(resource, 'ply');
}

export function validateStlBinaryResource(resource) {
    return validateGeometryBinaryResource(resource, 'stl');
}
